import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AdminAnalyticsBreakdownDto,
  AdminAnalyticsDashboardDto,
  getAdminAnalytics,
  useApiClient,
} from '../api/client';
import { MetricCard, PageHeader, PageShell, cx } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';
import { InterestAnalyticsTab } from '../components/InterestAnalyticsTab';
import { ActivityChart } from '../components/AnalyticsActivityChart';
import { PushReachabilityPanel } from '../components/PushReachabilityPanel';

type Tab = 'overview' | 'users' | 'onboarding' | 'interests' | 'push' | 'marketplace' | 'groups' | 'communities' | 'guidedTour';

const TIME_ZONE = 'Europe/Vienna';

function isoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function rangeForDays(days: number) {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - days + 1);
  return { from: isoDate(from), to: isoDate(to) };
}

function formatNumber(value: number) {
  return new Intl.NumberFormat().format(value);
}

function formatPercent(value: number) {
  return `${value.toFixed(1)}%`;
}

const Panel: React.FC<{ title: React.ReactNode; description?: React.ReactNode; children: React.ReactNode; className?: string }> = ({
  title,
  description,
  children,
  className,
}) => (
  <section className={cx('rounded-xl border border-gray-200 bg-white p-5 shadow-sm', className)}>
    <div className="mb-4">
      <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
    </div>
    {children}
  </section>
);


const HorizontalBars: React.FC<{ items: Array<{ key: string; label: string; value: number }>; emptyLabel: string }> = ({ items, emptyLabel }) => {
  const max = Math.max(1, ...items.map((item) => item.value));
  if (!items.length) return <div className="py-8 text-center text-sm text-gray-500">{emptyLabel}</div>;
  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={item.key}>
          <div className="mb-1 flex items-center justify-between gap-3 text-sm">
            <span className="truncate font-medium text-gray-700" title={item.label}>{item.label}</span>
            <span className="font-bold text-gray-900">{formatNumber(item.value)}</span>
          </div>
          <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
            <div
              className={cx('h-full rounded-full', index === 0 ? 'bg-secondary' : 'bg-secondary/60')}
              style={{ width: `${Math.max(item.value > 0 ? 2 : 0, item.value / max * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const BreakdownPanel: React.FC<{
  title: string;
  items: AdminAnalyticsBreakdownDto[];
  labelFor: (key: string) => string;
  emptyLabel: string;
}> = ({ title, items, labelFor, emptyLabel }) => (
  <Panel title={title}>
    <HorizontalBars
      items={items.map((item) => ({ key: item.key, label: labelFor(item.key), value: item.users }))}
      emptyLabel={emptyLabel}
    />
  </Panel>
);

const GoalCard: React.FC<{ label: string; value: number; target: number; description: string }> = ({ label, value, target, description }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
    <div className="text-sm font-semibold text-gray-700">{label}</div>
    <div className="mt-2 text-2xl font-extrabold text-gray-900">
      {formatNumber(value)} <span className="text-base font-normal text-gray-500">/ {formatNumber(target)}</span>
    </div>
    <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200" role="progressbar"
      aria-label={label} aria-valuemin={0} aria-valuemax={target} aria-valuenow={Math.min(value, target)}
      aria-valuetext={`${formatNumber(value)} / ${formatNumber(target)}`}>
      <div className={cx('h-full rounded-full', value >= target ? 'bg-green-500' : 'bg-secondary')}
        style={{ width: `${Math.min(100, value / target * 100)}%` }} />
    </div>
    <p className="mt-2 text-xs leading-5 text-gray-500">{description}</p>
  </div>
);

export const AnalyticsDashboardPage: React.FC = () => {
  const { request } = useApiClient();
  const { t, language } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = searchParams.get('tab');
  const tab: Tab = ['users', 'onboarding', 'interests', 'push', 'marketplace', 'groups', 'communities', 'guidedTour'].includes(selectedTab ?? '') ? selectedTab as Tab : 'overview';
  const isInterestsTab = tab === 'interests';
  const usesDashboardData = !isInterestsTab && tab !== 'push';
  const [days, setDays] = React.useState<number | 'all'>(30);
  const [data, setData] = React.useState<AdminAnalyticsDashboardDto | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!usesDashboardData) return;
    let active = true;
    const range = days === 'all' ? { allTime: true as const } : rangeForDays(days);
    setLoading(true);
    setError(null);
    getAdminAnalytics(request, { ...range, timeZone: TIME_ZONE })
      .then((result) => { if (active) setData(result); })
      .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : String(reason)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [days, request, usesDashboardData]);

  const labels = data?.activity.map((day) => day.date) ?? [];
  const communityNetGrowth = data?.communities.items.reduce((sum, item) => sum + item.netGrowth, 0) ?? 0;
  const onboardingDropOffs = data?.users.onboardingDropOffs ?? [];
  const openOnboardingUsers = onboardingDropOffs.reduce((sum, item) => sum + item.users, 0);
  const biggestOnboardingDropOff = onboardingDropOffs[0];
  const devicePlatforms = data?.devicePlatforms;
  const dashboardVisits = data?.communities.dashboardVisits;
  const visitTime = (value: string) => new Date(value).toLocaleString(language === 'de' ? 'de-AT' : 'en-GB', {
    timeZone: data?.timeZone ?? TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'overview', label: t('analytics.tab.overview') },
    { id: 'users', label: t('analytics.tab.users') },
    { id: 'onboarding', label: t('analytics.tab.onboarding') },
    { id: 'interests', label: t('analytics.tab.interests') },
    { id: 'push', label: t('analytics.push.title') },
    { id: 'marketplace', label: t('analytics.tab.marketplace') },
    { id: 'groups', label: t('analytics.tab.groups') },
    { id: 'communities', label: t('analytics.tab.communities') },
    { id: 'guidedTour', label: t('analytics.tab.guidedTour') },
  ];
  const breakdownLabel = (key: string) => t(`analytics.value.${key}`) === `analytics.value.${key}` ? key : t(`analytics.value.${key}`);

  return (
    <PageShell>
      <PageHeader
        title={t('analytics.title')}
        description={t('analytics.description')}
        actions={!isInterestsTab && (
          <div className="inline-flex flex-wrap rounded-lg border border-gray-200 bg-white p-1 shadow-sm" aria-label={t('analytics.period')}>
            {([7, 30, 90, 'all'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setDays(value)}
                aria-pressed={days === value}
                className={cx('rounded-md px-3 py-2 text-sm font-semibold transition-colors', days === value ? 'bg-secondary text-white' : 'text-gray-600 hover:bg-gray-100')}
              >
                {value === 'all' ? t('analytics.allTime') : t('analytics.lastDays', { days: value })}
              </button>
            ))}
          </div>
        )}
        meta={usesDashboardData && data && <span className="text-xs text-gray-500">{data.from} – {data.to} · {data.timeZone}</span>}
      />

      <div className="mb-6 overflow-x-auto border-b border-gray-200" role="tablist" aria-label={t('analytics.categories')}>
        <div className="flex min-w-max gap-6">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              onClick={() => setSearchParams((previous) => {
                const next = new URLSearchParams(previous);
                next.set('tab', item.id);
                return next;
              })}
              className={cx(
                'border-b-2 px-1 pb-3 text-sm font-bold transition-colors',
                tab === item.id ? 'border-secondary text-secondary' : 'border-transparent text-gray-500 hover:text-gray-900'
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {isInterestsTab && <InterestAnalyticsTab />}
      {tab === 'push' && (
        <div role="tabpanel" aria-label={t('analytics.push.title')}>
          <PushReachabilityPanel days={days} />
        </div>
      )}
      {usesDashboardData && loading && <div className="rounded-xl border bg-white p-12 text-center text-gray-500">{t('analytics.loading')}</div>}
      {usesDashboardData && error && !loading && <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-700">{t('analytics.error')}: {error}</div>}
      {usesDashboardData && !loading && !error && data && (
        <>
          {tab === 'overview' && (
            <div className="space-y-6" role="tabpanel">
              <section aria-label={t('analytics.goals.title')}>
                <Panel title={t('analytics.goals.title')} description={t('analytics.goals.scope')}>
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <GoalCard label={t('analytics.goals.users')} value={data.goals.registeredUsers} target={1000} description={t('analytics.goals.usersHint')} />
                    <GoalCard label={t('analytics.goals.series')} value={data.goals.activeRideSeries} target={50}
                      description={t('analytics.goals.seriesHint', { groups: formatNumber(data.goals.groupRideSeries), public: formatNumber(data.goals.nonGroupRideSeries) })} />
                    <GoalCard label={t('analytics.goals.rides')} value={data.goals.completedNonGroupRides} target={100} description={t('analytics.goals.ridesHint')} />
                    <GoalCard label={t('analytics.goals.groups')} value={data.goals.activeGroups} target={10} description={t('analytics.groups.activeHint')} />
                  </div>
                </Panel>
              </section>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <MetricCard label={t('analytics.metric.totalUsers')} value={formatNumber(data.overview.totalUsers)} tone="slate" />
                <MetricCard label={t('analytics.metric.newUsers')} value={formatNumber(data.overview.newUsers)} tone="blue" />
                <MetricCard label={t('analytics.metric.activeUsers')} value={formatNumber(data.overview.activeUsers)} tone="green" />
                <MetricCard label={t('analytics.metric.completedTrips')} value={formatNumber(data.overview.completedBookings)} tone="violet" />
              </div>
              <div className="grid gap-6 xl:grid-cols-3">
                <Panel title={t('analytics.activity.title')} description={t('analytics.activity.description')} className="xl:col-span-2">
                  <ActivityChart labels={labels} series={[
                    { label: t('analytics.metric.newUsers'), color: '#2563eb', values: data.activity.map((day) => day.newUsers) },
                    { label: t('analytics.metric.activeUsers'), color: '#16a34a', values: data.activity.map((day) => day.activeUsers) },
                    { label: t('analytics.metric.completedTrips'), color: '#7c3aed', values: data.activity.map((day) => day.completedBookings) },
                  ]} />
                </Panel>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
                  <MetricCard label={t('analytics.metric.onboardingRate')} value={formatPercent(data.overview.onboardingCompletionRate)} tone="amber" />
                  <MetricCard
                    label={t('analytics.metric.phoneCoverage')}
                    value={(
                      <>
                        <span>{data.overview.newUsers === 0 ? '—' : formatPercent(data.overview.phoneNumberCoverageRate)}</span>
                        <div className="mt-2 text-xs font-normal leading-5">
                          <div>{t('analytics.phoneCoverage.count', {
                            count: formatNumber(data.overview.newUsersWithPhone),
                            total: formatNumber(data.overview.newUsers),
                          })}</div>
                        </div>
                      </>
                    )}
                    tone="green"
                  />
                  <MetricCard
                    label={t('analytics.metric.verificationRate')}
                    value={(
                      <>
                        <div className="flex items-baseline justify-between gap-3 font-extrabold">
                          <span className="text-base">Overall</span>
                          <span>{data.overview.newUsers === 0 ? '—' : formatPercent(data.overview.verificationRate)}</span>
                        </div>
                        <div className="mt-2 space-y-1 text-sm font-normal leading-5">
                          <div className="flex justify-between gap-3">
                            <span>Didit</span>
                            <span>{data.overview.newUsers === 0 ? '—' : formatPercent(data.overview.diditVerificationRate)}</span>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span>Stripe</span>
                            <span>{data.overview.newUsers === 0 ? '—' : formatPercent(data.overview.stripeVerificationRate)}</span>
                          </div>
                        </div>
                        <div className="mt-2 text-xs font-normal leading-5">
                          <div>{t('analytics.verification.count', {
                            count: formatNumber(data.overview.newUsersVerified),
                            total: formatNumber(data.overview.newUsers),
                          })}</div>
                        </div>
                      </>
                    )}
                    tone="violet"
                  />
                  <MetricCard label={t('analytics.metric.rideSearches')} value={formatNumber(data.overview.rideSearches)} tone="blue" />
                  <Panel title={t('analytics.platforms.title')} description={t('analytics.platforms.description')}>
                    {!devicePlatforms?.knownInstallations ? (
                      <div className="py-4 text-center text-sm text-gray-500">{t('analytics.noData')}</div>
                    ) : (
                      <div
                        aria-label={t('analytics.platforms.distribution', {
                          ios: formatPercent(devicePlatforms.iosPercentage),
                          android: formatPercent(devicePlatforms.androidPercentage),
                        })}
                      >
                        <div className="mb-3 flex h-3 overflow-hidden rounded-full bg-gray-100">
                          <div className="bg-gray-900" style={{ width: `${devicePlatforms.iosPercentage}%` }} />
                          <div className="bg-green-500" style={{ width: `${devicePlatforms.androidPercentage}%` }} />
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between gap-3">
                            <span className="inline-flex items-center gap-2 font-medium text-gray-700">
                              <span className="h-2.5 w-2.5 rounded-full bg-gray-900" />iOS
                            </span>
                            <span className="font-bold text-gray-900">{formatPercent(devicePlatforms.iosPercentage)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="inline-flex items-center gap-2 font-medium text-gray-700">
                              <span className="h-2.5 w-2.5 rounded-full bg-green-500" />Android
                            </span>
                            <span className="font-bold text-gray-900">{formatPercent(devicePlatforms.androidPercentage)}</span>
                          </div>
                        </div>
                        <div className="mt-3 text-xs text-gray-500">
                          {t('analytics.platforms.knownInstallations', { count: formatNumber(devicePlatforms.knownInstallations) })}
                        </div>
                      </div>
                    )}
                  </Panel>
                  {data.overview.usageDataSince && (
                    <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-xs leading-5 text-blue-800">
                      {t('analytics.dataSince', { date: new Date(data.overview.usageDataSince).toLocaleDateString() })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === 'users' && (
            <div className="space-y-6" role="tabpanel">
              <Panel title={t('analytics.users.funnel')} description={t('analytics.users.funnelDescription')}>
                <div className="space-y-4">
                  {data.users.funnel.map((step, index) => {
                    const base = data.users.funnel[0]?.users || 0;
                    const percent = base ? step.users / base * 100 : 0;
                    return (
                      <div key={step.key} className="grid items-center gap-2 sm:grid-cols-[220px_1fr_90px]">
                        <div className="text-sm font-semibold text-gray-700">{breakdownLabel(step.key)}</div>
                        <div className="h-8 overflow-hidden rounded-md bg-gray-100">
                          <div
                            className="flex h-full items-center rounded-md bg-secondary/80 px-3 text-xs font-bold text-white transition-all"
                            style={{ width: `${Math.max(percent > 0 ? 4 : 0, percent)}%` }}
                          >
                            {index === 0 ? '' : formatPercent(percent)}
                          </div>
                        </div>
                        <div className="text-right text-sm font-bold text-gray-900">{formatNumber(step.users)}</div>
                      </div>
                    );
                  })}
                </div>
              </Panel>
              <div className="grid gap-6 lg:grid-cols-3">
                <BreakdownPanel title={t('analytics.users.acquisition')} items={data.users.acquisitionSources} labelFor={breakdownLabel} emptyLabel={t('analytics.noData')} />
                <BreakdownPanel title={t('analytics.users.mobilityRole')} items={data.users.mobilityRoles} labelFor={breakdownLabel} emptyLabel={t('analytics.noData')} />
                <BreakdownPanel title={t('analytics.users.language')} items={data.users.languages} labelFor={(key) => key.toUpperCase()} emptyLabel={t('analytics.noData')} />
              </div>
            </div>
          )}

          {tab === 'onboarding' && (
            <div className="space-y-6" role="tabpanel">
              <div className="grid gap-4 sm:grid-cols-3">
                <MetricCard label={t('analytics.onboarding.open')} value={formatNumber(openOnboardingUsers)} tone="amber" />
                <MetricCard label={t('analytics.onboarding.completion')} value={formatPercent(data.overview.onboardingCompletionRate)} tone="green" />
                <MetricCard
                  label={t('analytics.onboarding.biggest')}
                  value={biggestOnboardingDropOff ? breakdownLabel(biggestOnboardingDropOff.key) : '—'}
                  tone="red"
                />
              </div>
              <Panel title={t('analytics.onboarding.steps')} description={t('analytics.onboarding.description')}>
                <HorizontalBars
                  items={onboardingDropOffs.map((item) => ({
                    key: item.key,
                    label: `${breakdownLabel(item.key)} · ${formatPercent(openOnboardingUsers ? item.users / openOnboardingUsers * 100 : 0)}`,
                    value: item.users,
                  }))}
                  emptyLabel={t('analytics.noData')}
                />
              </Panel>
            </div>
          )}

          {tab === 'marketplace' && (
            <div className="space-y-6" role="tabpanel">
              <p className="text-sm text-gray-500">{t('analytics.marketplace.scope')}</p>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                <MetricCard label={t('analytics.metric.rideSearches')} value={formatNumber(data.marketplace.rideSearches)} tone="blue" />
                <MetricCard label={t('analytics.marketplace.zeroResults')} value={formatPercent(data.marketplace.zeroResultRate)} tone="red" />
                <MetricCard label={t('analytics.marketplace.requests')} value={formatNumber(data.marketplace.rideRequests)} tone="amber" />
                <MetricCard label={t('analytics.marketplace.publicRides')} value={formatNumber(data.marketplace.rides)} tone="green" />
                <MetricCard label={t('analytics.rides.completed')} value={formatNumber(data.marketplace.completedRides)} tone="violet" />
              </div>
              <div className="grid gap-6 xl:grid-cols-3">
                <Panel title={t('analytics.marketplace.activity')} description={t('analytics.marketplace.activityDescription')} className="xl:col-span-2">
                  <ActivityChart labels={data.marketplace.activity.map((day) => day.date)} series={[
                    { label: t('analytics.rides.offered'), color: '#2563eb', values: data.marketplace.activity.map((day) => day.offeredRides) },
                    { label: t('analytics.rides.completed'), color: '#16a34a', values: data.marketplace.activity.map((day) => day.completedRides) },
                    { label: t('analytics.rides.passengers'), color: '#7c3aed', values: data.marketplace.activity.map((day) => day.completedPassengerTrips) },
                  ]} />
                </Panel>
                <Panel title={t('analytics.marketplace.searchQuality')}>
                  <dl className="divide-y divide-gray-100">
                    {[
                      [t('analytics.marketplace.successfulSearches'), data.marketplace.successfulSearches],
                      [t('analytics.marketplace.zeroResultSearches'), data.marketplace.zeroResultSearches],
                      [t('analytics.marketplace.avgResults'), data.marketplace.averageSearchResults.toFixed(1)],
                      [t('analytics.marketplace.bookings'), data.marketplace.bookings],
                      [t('analytics.rides.passengers'), data.marketplace.completedPassengerTrips],
                      [t('analytics.marketplace.cancelledBookings'), data.marketplace.cancelledBookings],
                    ].map(([label, value]) => (
                      <div key={String(label)} className="flex items-center justify-between gap-4 py-3 text-sm">
                        <dt className="text-gray-500">{label}</dt>
                        <dd className="font-bold text-gray-900">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </Panel>
              </div>
            </div>
          )}

          {tab === 'groups' && (
            <div className="space-y-6" role="tabpanel">
              <p className="text-sm text-gray-500">{t('analytics.groups.scope')}</p>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                <MetricCard label={t('analytics.groups.total')} value={formatNumber(data.groups.totalGroups)} tone="slate" />
                <MetricCard label={t('analytics.goals.groups')} value={`${formatNumber(data.goals.activeGroups)} / 10`} tone="green" />
                <MetricCard label={t('analytics.rides.offered')} value={formatNumber(data.groups.offeredRides)} tone="blue" />
                <MetricCard label={t('analytics.rides.completed')} value={formatNumber(data.groups.completedRides)} tone="violet" />
                <MetricCard label={t('analytics.rides.passengers')} value={formatNumber(data.groups.completedPassengerTrips)} tone="amber" />
              </div>
              <Panel title={t('analytics.groups.activity')} description={t('analytics.groups.activityHint')}>
                <ActivityChart labels={data.groups.activity.map((day) => day.date)} series={[
                  { label: t('analytics.rides.offered'), color: '#2563eb', values: data.groups.activity.map((day) => day.offeredRides) },
                  { label: t('analytics.rides.completed'), color: '#16a34a', values: data.groups.activity.map((day) => day.completedRides) },
                  { label: t('analytics.rides.passengers'), color: '#7c3aed', values: data.groups.activity.map((day) => day.completedPassengerTrips) },
                ]} />
              </Panel>
              <Panel title={t('analytics.groups.details')} description={(
                <>{t('analytics.groups.activeHint')}<br />
                  {new Date(data.goals.activeWindowFrom).toLocaleString(undefined, { timeZone: TIME_ZONE })} – {new Date(data.goals.activeWindowTo).toLocaleString(undefined, { timeZone: TIME_ZONE })} · {TIME_ZONE}
                </>
              )}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm" aria-label={t('analytics.groups.details')}>
                    <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3">{t('analytics.groups.group')}</th>
                        <th className="px-4 py-3">{t('analytics.groups.status')}</th>
                        <th className="px-4 py-3 text-right">{t('analytics.groups.last7Rides')}</th>
                        <th className="px-4 py-3 text-right">{t('analytics.groups.last7Passengers')}</th>
                        <th className="px-4 py-3 text-right">{t('analytics.rides.offered')}</th>
                        <th className="px-4 py-3 text-right">{t('analytics.rides.completed')}</th>
                        <th className="px-4 py-3 text-right">{t('analytics.rides.passengers')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.groups.items.map((group) => (
                        <tr key={group.groupId} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <Link to={`/communities/${group.communityId}/groups/${group.groupId}`} className="font-semibold hover:text-secondary hover:underline">{group.groupName}</Link>
                            <div className="text-xs text-gray-500">{group.communityName}</div>
                          </td>
                          <td className="px-4 py-3"><span className={cx('rounded-full px-2 py-1 text-xs font-semibold', group.completedRidesLast7Days >= 5 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600')}>
                            {t(group.completedRidesLast7Days >= 5 ? 'analytics.groups.active' : 'analytics.groups.belowThreshold')}
                          </span></td>
                          <td className="px-4 py-3 text-right font-bold">{formatNumber(group.completedRidesLast7Days)} / 5</td>
                          <td className="px-4 py-3 text-right">{formatNumber(group.completedPassengerTripsLast7Days)}</td>
                          <td className="px-4 py-3 text-right">{formatNumber(group.offeredRides)}</td>
                          <td className="px-4 py-3 text-right">{formatNumber(group.completedRides)}</td>
                          <td className="px-4 py-3 text-right">{formatNumber(group.completedPassengerTrips)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!data.groups.items.length && <div className="py-8 text-center text-sm text-gray-500">{t('analytics.noData')}</div>}
                </div>
              </Panel>
            </div>
          )}

          {tab === 'communities' && (
            <div className="space-y-6" role="tabpanel">
              {dashboardVisits && (
                <Panel title={t('analytics.managerVisits.title')} description={t('analytics.managerVisits.description', { timeZone: data.timeZone })}>
                  <div className="mb-5 grid gap-4 sm:grid-cols-2">
                    <MetricCard label={t('analytics.managerVisits.visits')} value={formatNumber(dashboardVisits.totalVisits)} tone="blue" />
                    <MetricCard label={t('analytics.managerVisits.managers')} value={formatNumber(dashboardVisits.uniqueManagers)} tone="slate" />
                  </div>
                  <ActivityChart labels={dashboardVisits.daily.map((day) => day.date)}
                    series={[{ label: t('analytics.managerVisits.visits'), color: '#6b2bd9', values: dashboardVisits.daily.map((day) => day.visits) }]} />
                  <h3 className="mb-3 mt-6 font-semibold text-gray-900">{t('analytics.managerVisits.byDay')}</h3>
                  <div className="max-h-96 overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                          <th scope="col" className="px-4 py-3">{t('analytics.managerVisits.day')}</th>
                          <th scope="col" className="px-4 py-3">{t('analytics.managerVisits.manager')}</th>
                          <th scope="col" className="px-4 py-3">{t('analytics.communities.community')}</th>
                          <th scope="col" className="px-4 py-3 text-right">{t('analytics.managerVisits.visits')}</th>
                          <th scope="col" className="px-4 py-3">{t('analytics.managerVisits.first')}</th>
                          <th scope="col" className="px-4 py-3">{t('analytics.managerVisits.last')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {dashboardVisits.managers.map((item) => (
                          <tr key={`${item.date}:${item.communityId}:${item.managerId}`}>
                            <td className="whitespace-nowrap px-4 py-3">{item.date}</td>
                            <td className="px-4 py-3 font-medium">{item.managerName || item.managerId}</td>
                            <td className="px-4 py-3">{item.communityName}</td>
                            <td className="px-4 py-3 text-right">{formatNumber(item.visits)}</td>
                            <td className="whitespace-nowrap px-4 py-3"><time dateTime={item.firstVisit}>{visitTime(item.firstVisit)}</time></td>
                            <td className="whitespace-nowrap px-4 py-3"><time dateTime={item.lastVisit}>{visitTime(item.lastVisit)}</time></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {!dashboardVisits.managers.length && <p className="py-6 text-center text-sm text-gray-500">{t('analytics.managerVisits.empty')}</p>}
                  </div>
                  <h3 className="mb-3 mt-6 font-semibold text-gray-900">{t('analytics.managerVisits.recent')}</h3>
                  <div className="max-h-64 overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                          <th scope="col" className="px-4 py-3">{t('analytics.managerVisits.when')}</th>
                          <th scope="col" className="px-4 py-3">{t('analytics.managerVisits.manager')}</th>
                          <th scope="col" className="px-4 py-3">{t('analytics.communities.community')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {dashboardVisits.recent.map((item) => (
                          <tr key={`${item.managerId}:${item.communityId}:${item.visitId}`}>
                            <td className="whitespace-nowrap px-4 py-3"><time dateTime={item.visitedAt}>{visitTime(item.visitedAt)}</time></td>
                            <td className="px-4 py-3 font-medium">{item.managerName || item.managerId}</td>
                            <td className="px-4 py-3">{item.communityName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              )}
              <div className="grid gap-4 sm:grid-cols-3">
                <MetricCard label={t('analytics.communities.total')} value={formatNumber(data.communities.totalCommunities)} tone="slate" />
                <MetricCard label={t('analytics.communities.memberships')} value={formatNumber(data.communities.currentMemberships)} tone="blue" />
                <MetricCard
                  label={t('analytics.communities.netGrowth')}
                  value={`${communityNetGrowth > 0 ? '+' : ''}${formatNumber(communityNetGrowth)}`}
                  tone={communityNetGrowth > 0 ? 'green' : communityNetGrowth < 0 ? 'red' : 'slate'}
                />
              </div>
              <div className="grid gap-6 xl:grid-cols-2">
                <Panel title={t('analytics.communities.byUsers')} description={t('analytics.communities.byUsersDescription')}>
                  <HorizontalBars
                    items={data.communities.items.slice(0, 12).map((item) => ({ key: item.communityId, label: item.communityName, value: item.currentMembers }))}
                    emptyLabel={t('analytics.noData')}
                  />
                </Panel>
                <Panel title={t('analytics.communities.growth')} description={t('analytics.communities.growthDescription')}>
                  <div className="space-y-3">
                    {data.communities.items.slice().sort((a, b) => b.netGrowth - a.netGrowth).slice(0, 12).map((item) => {
                      const maxGrowth = Math.max(1, ...data.communities.items.map((community) => Math.abs(community.netGrowth)));
                      return (
                        <div key={item.communityId}>
                          <div className="mb-1 flex justify-between gap-3 text-sm">
                            <span className="truncate font-medium text-gray-700">{item.communityName}</span>
                            <span className={cx('font-bold', item.netGrowth > 0 ? 'text-green-700' : item.netGrowth < 0 ? 'text-red-700' : 'text-gray-500')}>
                              {item.netGrowth > 0 ? '+' : ''}{item.netGrowth}
                            </span>
                          </div>
                          <div className="h-2.5 rounded-full bg-gray-100">
                            <div
                              className={cx('h-full rounded-full', item.netGrowth >= 0 ? 'bg-green-500' : 'bg-red-500')}
                              style={{ width: `${Math.max(item.netGrowth === 0 ? 0 : 2, Math.abs(item.netGrowth) / maxGrowth * 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {!data.communities.items.length && <div className="py-8 text-center text-sm text-gray-500">{t('analytics.noData')}</div>}
                  </div>
                </Panel>
              </div>
              <Panel title={t('analytics.communities.details')}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3">{t('analytics.communities.community')}</th>
                        <th className="px-4 py-3 text-right">{t('analytics.communities.current')}</th>
                        <th className="px-4 py-3 text-right">{t('analytics.communities.period')}</th>
                        <th className="px-4 py-3 text-right">{t('analytics.communities.joined')}</th>
                        <th className="px-4 py-3 text-right">{t('analytics.communities.left')}</th>
                        <th className="px-4 py-3 text-right">{t('analytics.communities.net')}</th>
                        <th className="px-4 py-3 text-right">{t('analytics.communities.rate')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.communities.items.map((item) => (
                        <tr key={item.communityId} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-semibold text-gray-900">
                            <Link to={`/communities/${item.communityId}`} className="hover:text-secondary hover:underline">{item.communityName}</Link>
                          </td>
                          <td className="px-4 py-3 text-right">{item.currentMembers}</td>
                          <td className="px-4 py-3 text-right text-gray-500">{item.membersAtPeriodStart} → {item.membersAtPeriodEnd}</td>
                          <td className="px-4 py-3 text-right text-green-700">+{item.joinedUsers}</td>
                          <td className="px-4 py-3 text-right text-red-700">−{item.leftUsers}</td>
                          <td className={cx('px-4 py-3 text-right font-bold', item.netGrowth > 0 ? 'text-green-700' : item.netGrowth < 0 ? 'text-red-700' : 'text-gray-500')}>
                            {item.netGrowth > 0 ? '+' : ''}{item.netGrowth}
                          </td>
                          <td className="px-4 py-3 text-right">{item.growthRate == null ? '—' : formatPercent(item.growthRate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>
          )}

          {tab === 'guidedTour' && (
            <div className="space-y-6" role="tabpanel">
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <MetricCard label={t('analytics.guidedTour.totalRuns')} value={formatNumber(data.guidedTour.totalRuns)} tone="slate" />
                <MetricCard label={t('analytics.guidedTour.uniqueUsers')} value={formatNumber(data.guidedTour.uniqueUsers)} tone="blue" />
                <MetricCard label={t('analytics.guidedTour.completionRate')} value={formatPercent(data.guidedTour.completionRate)} tone="green" />
                <MetricCard
                  label={t('analytics.guidedTour.skipped')}
                  value={`${formatNumber(data.guidedTour.skippedRuns)} · ${formatPercent(data.guidedTour.skipRate)}`}
                  tone="amber"
                />
                <MetricCard label={t('analytics.guidedTour.abandoned')} value={formatNumber(data.guidedTour.abandonedRuns)} tone="red" />
                <MetricCard label={t('analytics.guidedTour.inProgress')} value={formatNumber(data.guidedTour.inProgressRuns)} tone="violet" />
              </div>
              <div className="grid gap-6 lg:grid-cols-3">
                <Panel title={t('analytics.guidedTour.outcomes')} description={t('analytics.guidedTour.outcomesDescription')}>
                  <HorizontalBars
                    items={data.guidedTour.outcomes.map((item) => ({ key: item.key, label: breakdownLabel(item.key), value: item.runs }))}
                    emptyLabel={t('analytics.noData')}
                  />
                </Panel>
                <Panel title={t('analytics.guidedTour.dropOffs')} description={t('analytics.guidedTour.dropOffsDescription')}>
                  <HorizontalBars
                    items={data.guidedTour.dropOffs.map((item) => ({ key: item.key, label: breakdownLabel(item.key), value: item.runs }))}
                    emptyLabel={t('analytics.noData')}
                  />
                </Panel>
                <Panel title={t('analytics.guidedTour.branches')} description={t('analytics.guidedTour.branchesDescription')}>
                  <HorizontalBars
                    items={data.guidedTour.branches.map((item) => ({ key: item.key, label: breakdownLabel(item.key), value: item.runs }))}
                    emptyLabel={t('analytics.noData')}
                  />
                </Panel>
              </div>
            </div>
          )}
        </>
      )}
    </PageShell>
  );
};
